import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store'
import { useLifeCanvas } from '../hooks/useLifeCanvas'
import { totalWeeks, weeksLived, fmtYearsMo } from '../lib/timeline'
import { CONFIG } from '../config/config'

// ── Step order (resequenced): intro → dob → lifespan → cap → hours → audit → goal
const STEP_COUNT = 7

export default function OnboardingPage() {
  const navigate         = useNavigate()
  const saveProfile      = useStore(s => s.saveProfile)
  const saveAudit        = useStore(s => s.saveAudit)
  const finishOnboarding = useStore(s => s.finishOnboarding)
  const createGoal       = useStore(s => s.createGoal)
  const afterMutation    = useStore(s => s.afterMutation)

  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [err,  setErr]  = useState('')

  // All form state lives here so canvas can react to changes live
  const [dob,      setDob]      = useState('')
  const [lifespan, setLifespan] = useState(String(CONFIG.DEFAULT_LIFESPAN))
  const [hours,    setHours]    = useState('')
  const [cap,      setCap]      = useState('3')
  const [audit,    setAudit]    = useState(
    CONFIG.AUDIT_DOMAINS.reduce((a, d) => ({ ...a, [d]: 5 }), {})
  )

  const demoRef = useRef(null)
  const liveRef = useRef(null)

  const ls          = Math.max(40, Math.min(120, Number(lifespan) || CONFIG.DEFAULT_LIFESPAN))
  const demoProfile = { date_of_birth: '1990-01-01', expected_lifespan: ls }
  const liveProfile = dob ? { date_of_birth: dob, expected_lifespan: ls } : null

  useLifeCanvas(demoRef, { profile: demoProfile, goals: [], zoom: step === 0 ? 'week' : 'month' })
  useLifeCanvas(liveRef, { profile: liveProfile, goals: [], zoom: 'month' })

  const total     = totalWeeks(ls)
  const lived     = dob ? weeksLived(dob) : 0
  const remaining = total - lived

  // Validation per step
  const canProceed = [
    true,                                         // 0 intro
    dob.length > 0,                               // 1 dob
    ls >= 40 && ls <= 120,                        // 2 lifespan
    true,                                         // 3 cap
    Number(hours) > 0,                            // 4 hours
    true,                                         // 5 audit
    true,                                         // 6 goal (has skip)
  ]

  const handleNext = async () => {
    setErr('')
    // Step 5 (audit) = save everything before final step
    if (step === 5) {
      setBusy(true)
      const { error } = await saveProfile({
        date_of_birth:          dob,
        expected_lifespan:      ls,
        weekly_available_hours: Number(hours),
        active_goal_cap:        Number(cap),
      })
      if (error) { setBusy(false); setErr(error.message ?? 'Save failed'); return }
      await saveAudit(CONFIG.AUDIT_DOMAINS.map(d => ({ domain: d, score: audit[d] })))
      setBusy(false)
    }
    if (step < STEP_COUNT - 1) setStep(s => s + 1)
  }

  const finish = async (goalData) => {
    setBusy(true)
    if (goalData?.title?.trim()) {
      await createGoal({ ...goalData, bucket: 'committed' })
      await afterMutation()
    }
    await finishOnboarding()
    setBusy(false)
    navigate('/canvas', { replace: true })
  }

  return (
    // h-screen + overflow-hidden = button always visible, no page scroll
    <div className="h-screen bg-m-bg flex overflow-hidden">

      {/* ── LEFT: Canvas panel ── */}
      <div className="w-[45%] border-r border-m-border flex flex-col h-full">

        {/* Logo */}
        <div className="shrink-0 px-6 py-4 border-b border-m-border flex items-center gap-3">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-m-accent font-medium">MORI</span>
          <span className="font-mono text-[10px] text-m-subtle">what remains is yours</span>
        </div>

        {/* Canvas area — scrollable independently */}
        <div className="flex-1 overflow-auto p-6 flex flex-col gap-4">
          {step === 0 ? (
            <>
              <p className="font-mono text-[11px] text-m-subtle leading-relaxed">
                Each block below is one week of a human life.<br />
                The greyed ones are gone. The rest can still be claimed.
              </p>
              <canvas ref={demoRef} className="block max-w-full" style={{ imageRendering: 'pixelated' }} />
            </>
          ) : liveProfile ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="card p-3">
                  <p className="label mb-1">Remaining</p>
                  <p className="font-mono text-lg text-m-accent">{fmtYearsMo(remaining)}</p>
                </div>
                <div className="card p-3">
                  <p className="label mb-1">Lived</p>
                  <p className="font-mono text-lg text-m-subtle">{fmtYearsMo(lived)}</p>
                </div>
                <div className="card p-3">
                  <p className="label mb-1">Total weeks</p>
                  <p className="font-mono text-lg text-m-text">{total.toLocaleString()}</p>
                </div>
              </div>
              <canvas ref={liveRef} className="block max-w-full" style={{ imageRendering: 'pixelated' }} />
              <p className="font-mono text-[10px] text-m-faint">
                Darker blocks = past. Your data, your actual timeline.
              </p>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center border border-m-border">
              <p className="font-mono text-xs text-m-faint text-center px-4">
                Enter your date of birth to<br />personalize your canvas
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Form panel ── */}
      <div className="flex-1 flex flex-col h-full">

        {/* Progress bar */}
        <div className="shrink-0 px-6 py-4 border-b border-m-border">
          <div className="flex gap-1 mb-2">
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <div key={i}
                className={`h-0.5 flex-1 transition-all duration-300 ${i < step ? 'bg-m-accent' : i === step ? 'bg-m-accentDim' : 'bg-m-muted'}`}
              />
            ))}
          </div>
          <p className="font-mono text-[10px] text-m-faint">
            Step {step + 1} of {STEP_COUNT}
          </p>
        </div>

        {/* Step content — scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 py-8 max-w-md">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.18 }}
                className="flex flex-col gap-6"
              >
                {step === 0 && <StepIntro />}
                {step === 1 && <StepDob dob={dob} setDob={setDob} />}
                {step === 2 && <StepLifespan lifespan={lifespan} setLifespan={setLifespan} total={total} />}
                {step === 3 && <StepCap cap={cap} setCap={setCap} hours={hours} />}
                {step === 4 && <StepHours hours={hours} setHours={setHours} cap={cap} />}
                {step === 5 && <StepAudit audit={audit} setAudit={setAudit} />}
                {step === 6 && <StepGoal busy={busy} onFinish={finish} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* ── BUTTON — always visible, never clipped ── */}
        {step < 6 && (
          <div className="shrink-0 border-t border-m-border px-8 py-5 bg-m-bg">
            {err && <div className="alert-danger mb-3 text-xs">{err}</div>}
            <button
              onClick={handleNext}
              disabled={!canProceed[step] || busy}
              className="btn-primary w-full py-3 text-sm"
            >
              {busy ? 'Saving…' : step === 5 ? 'Save & Continue →' : 'Continue →'}
            </button>
            {step > 0 && (
              <button
                onClick={() => { setErr(''); setStep(s => s - 1) }}
                className="w-full mt-2 font-mono text-[10px] text-m-faint hover:text-m-subtle text-center py-1"
              >
                ← Back
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Step Components
// ────────────────────────────────────────────────────────────────────────────

function StepIntro() {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-mono text-3xl text-m-text leading-tight">
          Your time<br />is finite.
        </h1>
        <h1 className="font-mono text-3xl text-m-accent leading-tight mt-1">
          what remains<br />is yours.
        </h1>
      </div>
      <div className="divider" />
      <p className="font-mono text-sm text-m-subtle leading-relaxed">
        Mori is not a wishlist. Not a habit tracker. Not a motivation app.
      </p>
      <p className="font-mono text-sm text-m-text leading-relaxed">
        It is a system that forces you to confront what you are actually choosing to do with the time you have left — and what you are choosing not to.
      </p>
      <p className="font-mono text-xs text-m-faint leading-relaxed">
        Every block on the left is one week of a human life. Most are already gone. The rest will be spent whether you decide how or not.
      </p>
    </div>
  )
}

function StepDob({ dob, setDob }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">When were you born?</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          This anchors your canvas to your actual life. The blocks you've already lived will be marked.
        </p>
      </div>
      <div>
        <label className="label block mb-2">Date of birth</label>
        <input
          type="date"
          className="input text-base"
          value={dob}
          onChange={e => setDob(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          autoFocus
        />
      </div>
      {dob && (
        <div className="alert-accent">
          Your canvas will reflect your actual remaining time — not a generic template.
        </div>
      )}
    </div>
  )
}

function StepLifespan({ lifespan, setLifespan, total }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">How long do you expect to live?</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          Be honest. Not optimistic. Not pessimistic. This is your best estimate — it defines the boundary of your canvas.
        </p>
      </div>
      <div>
        <label className="label block mb-2">Expected lifespan (years)</label>
        <input
          type="number" min="40" max="120"
          className="input text-base"
          value={lifespan}
          onChange={e => setLifespan(e.target.value)}
          autoFocus
        />
        <p className="font-mono text-[10px] text-m-faint mt-1.5">Between 40 and 120</p>
      </div>
      {total > 0 && (
        <div className="card p-4 flex flex-col gap-1">
          <p className="label">Your canvas</p>
          <p className="font-mono text-2xl text-m-text">{total.toLocaleString()} weeks</p>
          <p className="font-mono text-[11px] text-m-faint">That's all you get. Not a day more.</p>
        </div>
      )}
    </div>
  )
}

function StepCap({ cap, setCap }) {
  const descriptions = {
    '3': 'Focused. Recommended for most people. Forces hard choices.',
    '4': 'Moderate. Works if your goals vary heavily in effort.',
    '5': 'Ambitious. High risk of spreading too thin.',
  }
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">How many goals at once?</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          This is the maximum number of goals you can actively work on at the same time. It is a hard limit — the system will enforce it.
        </p>
      </div>
      <div className="flex gap-3">
        {['3', '4', '5'].map(n => (
          <button key={n} onClick={() => setCap(n)}
            className={`flex-1 flex flex-col items-center gap-1 py-4 border transition-all ${
              cap === n
                ? 'border-m-accent bg-m-accent/5 text-m-accent'
                : 'border-m-border text-m-faint hover:border-m-dim hover:text-m-subtle'
            }`}>
            <span className="font-mono text-3xl">{n}</span>
            <span className="font-mono text-[9px] tracking-widest uppercase">goals</span>
          </button>
        ))}
      </div>
      {cap && (
        <div className="alert-info">
          <span className="font-mono text-[11px]">{descriptions[cap]}</span>
        </div>
      )}
      <p className="font-mono text-[10px] text-m-faint">
        You can adjust this after completing your first weekly review.
      </p>
    </div>
  )
}

function StepHours({ hours, setHours, cap }) {
  const total = Number(hours) || 0
  const perGoal = cap && total ? (total / Number(cap)).toFixed(1) : null
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">Hours per week for goal work?</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          How many hours per week can you realistically dedicate — accounting for work, sleep, family, and everything else? Be honest. Not aspirational.
        </p>
      </div>
      <div>
        <label className="label block mb-2">Hours / week</label>
        <input
          type="number" min="1" max="80" step="0.5"
          className="input text-base"
          placeholder="e.g. 15"
          value={hours}
          onChange={e => setHours(e.target.value)}
          autoFocus
        />
      </div>
      {total > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="card p-3">
            <p className="label mb-1">Per year</p>
            <p className="font-mono text-xl text-m-text">{(total * 52).toLocaleString()}h</p>
          </div>
          {perGoal && (
            <div className="card p-3">
              <p className="label mb-1">Per goal (avg)</p>
              <p className="font-mono text-xl text-m-accent">{perGoal}h/wk</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StepAudit({ audit, setAudit }) {
  const getColor = (score) => {
    if (score <= 3) return 'text-m-danger'
    if (score <= 6) return 'text-m-warn'
    return 'text-m-accent'
  }
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">Life audit</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          Rate each area of your life honestly. This is not a wellness survey — it's a baseline so you know where you're starting from.
        </p>
      </div>
      <div className="flex flex-col gap-5">
        {CONFIG.AUDIT_DOMAINS.map(d => (
          <div key={d}>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-xs text-m-text">{d}</label>
              <span className={`font-mono text-sm font-medium ${getColor(audit[d])}`}>
                {audit[d]}/10
              </span>
            </div>
            <input
              type="range" min="1" max="10" step="1"
              className="w-full h-1 cursor-pointer"
              value={audit[d]}
              onChange={e => setAudit(a => ({ ...a, [d]: Number(e.target.value) }))}
            />
            <div className="flex justify-between mt-1">
              <span className="font-mono text-[9px] text-m-faint">Critical</span>
              <span className="font-mono text-[9px] text-m-faint">Thriving</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StepGoal({ busy, onFinish }) {
  const [title,  setTitle]  = useState('')
  const [weeks,  setWeeks]  = useState('')
  const [hrs,    setHrs]    = useState('')
  const [effort, setEffort] = useState(2)

  const totalH = weeks && hrs ? Number(weeks) * Number(hrs) : null

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-mono text-2xl text-m-text">Your first committed goal</h2>
        <p className="font-mono text-xs text-m-subtle mt-2 leading-relaxed">
          One thing you are genuinely committing time to right now. Not a wish. Not "someday." Something that starts this week.
        </p>
      </div>

      <div>
        <label className="label block mb-2">What are you committing to?</label>
        <input
          className="input"
          placeholder="e.g. Learn to code in Python"
          value={title}
          onChange={e => setTitle(e.target.value)}
          autoFocus
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label block mb-2">Duration (weeks)</label>
          <input type="number" min="1" className="input" placeholder="e.g. 12"
            value={weeks} onChange={e => setWeeks(e.target.value)} />
        </div>
        <div>
          <label className="label block mb-2">Hours / week</label>
          <input type="number" min="0.5" step="0.5" className="input" placeholder="e.g. 5"
            value={hrs} onChange={e => setHrs(e.target.value)} />
        </div>
      </div>

      {totalH && (
        <div className="alert-accent">
          Total commitment: <strong className="font-mono">{totalH}h</strong> over {weeks} weeks.
        </div>
      )}

      <div>
        <label className="label block mb-2">Mental effort required</label>
        <div className="flex gap-2">
          {[[1,'Low','Routine'],[2,'Medium','Focused'],[3,'High','Deep work']].map(([v,l,d]) => (
            <button key={v} onClick={() => setEffort(v)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-3 border transition-all text-center ${
                effort === v
                  ? v === 1 ? 'border-m-faint text-m-subtle bg-m-muted/40'
                  : v === 2 ? 'border-m-warn/60 text-m-warn bg-m-warn/5'
                  :           'border-m-danger/60 text-m-danger bg-m-danger/5'
                  : 'border-m-border text-m-faint hover:border-m-dim'
              }`}>
              <span className="font-mono text-xs font-medium">{l}</span>
              <span className="font-mono text-[9px] opacity-70">{d}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <button
          onClick={() => onFinish({ title, estimated_weeks: weeks ? Number(weeks) : null, weekly_hours: hrs ? Number(hrs) : null, effort_level: effort })}
          disabled={!title.trim() || busy}
          className="btn-primary w-full py-3"
        >
          {busy ? 'Creating…' : 'Create goal & enter Mori'}
        </button>
        <button onClick={() => onFinish(null)} disabled={busy}
          className="w-full font-mono text-[10px] text-m-faint hover:text-m-subtle py-2 text-center transition-colors">
          Skip — I'll add goals later
        </button>
      </div>
    </div>
  )
}
